import React, { useState, useEffect } from 'react';

export type SaveButtonState = 'idle' | 'saving' | 'success' | 'error';

export interface SaveButtonProps {
  onSave: () => Promise<void>;
  disabled?: boolean;
  children?: React.ReactNode;
  className?: string;
  successMessage?: string;
  errorMessage?: string;
  successDuration?: number; // Duration to show success state in ms
  showSuccessFeedback?: boolean;
  showErrorFeedback?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary' | 'outline';
}

const SaveButton: React.FC<SaveButtonProps> = ({
  onSave,
  disabled = false,
  children = 'Save',
  className = '',
  successMessage = 'Saved successfully!',
  errorMessage = 'Save failed. Please try again.',
  successDuration = 2000,
  showSuccessFeedback = true,
  showErrorFeedback = true,
  size = 'md',
  variant = 'primary'
}) => {
  const [state, setState] = useState<SaveButtonState>('idle');
  const [message, setMessage] = useState<string>('');

  // Reset success state after duration
  useEffect(() => {
    if (state === 'success' && showSuccessFeedback) {
      const timer = setTimeout(() => {
        setState('idle');
        setMessage('');
      }, successDuration);
      return () => clearTimeout(timer);
    }
  }, [state, successDuration, showSuccessFeedback]);

  const handleSave = async () => {
    if (state === 'saving' || disabled) return;

    setState('saving');
    setMessage('');

    try {
      await onSave();
      setState('success');
      if (showSuccessFeedback) {
        setMessage(successMessage);
      }
    } catch (error) {
      setState('error');
      if (showErrorFeedback) {
        setMessage(errorMessage);
      }
      console.error('Save failed:', error);
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'px-3 py-1.5 text-sm';
      case 'lg':
        return 'px-6 py-3 text-lg';
      default:
        return 'px-4 py-2 text-sm';
    }
  };

  const getVariantClasses = () => {
    switch (variant) {
      case 'secondary':
        return 'bg-gray-600 text-white hover:bg-gray-700';
      case 'outline':
        return 'bg-transparent text-blue-600 border border-blue-600 hover:bg-blue-50';
      default:
        return 'bg-blue-600 text-white hover:bg-blue-700';
    }
  };

  const getStateClasses = () => {
    switch (state) {
      case 'saving':
        return 'opacity-75 cursor-not-allowed';
      case 'success':
        return 'bg-green-600 text-white hover:bg-green-700';
      case 'error':
        return 'bg-red-600 text-white hover:bg-red-700';
      default:
        return '';
    }
  };

  const getIcon = () => {
    switch (state) {
      case 'saving':
        return (
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2 inline-block" />
        );
      case 'success':
        return (
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getButtonText = () => {
    switch (state) {
      case 'saving':
        return 'Saving...';
      case 'success':
        return 'Saved!';
      case 'error':
        return 'Retry';
      default:
        return children;
    }
  };

  return (
    <div className="flex flex-col items-end">
      <button
        onClick={handleSave}
        disabled={disabled || state === 'saving'}
        className={`
          ${getSizeClasses()}
          ${getVariantClasses()}
          ${getStateClasses()}
          font-medium rounded-md transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
          ${className}
        `}
      >
        <div className="flex items-center">
          {getIcon()}
          {getButtonText()}
        </div>
      </button>

      {/* Feedback Message */}
      {message && (
        <div className={`
          mt-2 text-xs transition-opacity duration-200
          ${state === 'success' ? 'text-green-600' : 'text-red-600'}
        `}>
          {message}
        </div>
      )}
    </div>
  );
};

export default SaveButton;
